// components/ScrollManager.jsx
// One place that decides where a page starts vertically.
//
// Rules:
//   POP (back / forward)          restore where that entry was left
//   PUSH (a new page)             start at the top
//   REPLACE, same pathname        leave the scroll alone
//   REPLACE, different pathname   start at the top
//
// The split matters because REPLACE does two unrelated jobs here. Rewriting the
// query string is same-page state (a tab, an open accordion, the reader's resume
// section), and yanking someone to the top because they clicked a tab halfway
// down the page is not what they asked for. But a REPLACE that changes the
// pathname is a real move to a different page: the owner-to-library redirect,
// login landing on ?next=, the post-checkout navigation. Those are arrivals, and
// an arrival starts at the top. Turning a page in the reader is a PUSH, so it
// starts at the top either way.
//
// Two things about this app make the mechanics non-obvious.
//
// index.css puts `overflow-x: hidden` on a `body` with `height: 100%`, which per
// spec computes overflow-y to auto and makes <body> the scrolling box. So
// window.scrollY stays 0 and documentElement.scrollTop never moves: the real
// number is on document.body. Rather than guess which element scrolls, this
// reads whichever is non-zero and writes to both; the write is a no-op on the
// one that is not the scroller.
//
// And index.css sets `scroll-behavior: smooth` on html, which applies to
// programmatic scrolls too, including plain `scrollTop = 0` assignments. Every
// scroll here passes behavior "instant" so restoring a position does not animate
// a long glide down the page. The CSS rule stays, because the marketing pages'
// #offer / #apply / #tracks anchor links rely on it.
//
// The position is sampled on a timer rather than tracked with a scroll listener,
// because in this layout Chrome fires no scroll event at all when <body> scrolls:
// not on window in the capture phase, not on document, not on body itself. It was
// verified in the browser - body.scrollTop moves the page (a footer measured at
// 840px moved to 760px) while documentElement.scrollTop does nothing and no
// listener ever fires. Since document.scrollingElement is <html> but <html> is not
// the box that scrolls, the event has nowhere it is willing to go. Sampling does
// not care why.
import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

const KEY_PREFIX = "sc_scroll:";

// How long to keep trying to restore a position while the page's data arrives.
// A restore attempted before the content renders lands at 0, because the
// document is not tall enough to hold the offset yet.
//
// A timer, not requestAnimationFrame. rAF does not fire in a hidden tab, and a
// restore can easily be scheduled in one: a background tab that gets a
// visibilitychange-driven refresh, or a restored session. That would leave the
// position silently never applied. Timers are throttled in the background but
// they do run. Verified in the browser: in a hidden tab an rAF callback never
// fired while a setTimeout did.
const RESTORE_TIMEOUT_MS = 1500;
const RESTORE_STEP_MS = 16;

// How often to record where the page is. Losing up to this much of a scroll is
// the worst case, and it costs one property read.
const SAMPLE_MS = 150;

const readTop = () =>
  document.body.scrollTop ||
  document.documentElement.scrollTop ||
  window.scrollY ||
  0;

const writeTop = (top) => {
  document.body.scrollTo({ top, behavior: "instant" });
  document.documentElement.scrollTo({ top, behavior: "instant" });
};

// True once the document is tall enough for `top` to be a real position rather
// than a clamp to the bottom of a half-rendered page.
const canReach = (top) =>
  [document.body, document.documentElement].some(
    (el) => el && el.scrollHeight - el.clientHeight >= top - 1,
  );

const save = (key, top) => {
  if (!key) return;
  try {
    sessionStorage.setItem(KEY_PREFIX + key, String(top));
  } catch {
    // Blocked storage just means no restoration. Not worth surfacing.
  }
};

const load = (key) => {
  if (!key) return null;
  try {
    const raw = sessionStorage.getItem(KEY_PREFIX + key);
    if (raw == null) return null;
    const top = Number(raw);
    return Number.isFinite(top) && top >= 0 ? top : null;
  } catch {
    return null;
  }
};

export default function ScrollManager() {
  const location = useLocation();
  const navigationType = useNavigationType();

  const topRef = useRef(0);
  const previousKeyRef = useRef(null);
  const previousPathnameRef = useRef(location.pathname);
  const cancelRestoreRef = useRef(null);

  // The browser's own restoration races this one and wins sometimes, which looks
  // like a random jump. Ours is the only one that knows about ?s= and ?tab=.
  useEffect(() => {
    if (!("scrollRestoration" in window.history)) return undefined;
    const previous = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";
    return () => {
      window.history.scrollRestoration = previous;
    };
  }, []);

  // Record where the page is, so the position is already known when the user
  // navigates away. Sampled rather than listened for: see the note at the top of
  // this file, no scroll event is dispatched for this layout. The input events are
  // here only to catch up promptly right before a click, which is the moment the
  // value actually gets used.
  useEffect(() => {
    let timer = 0;
    const sample = () => {
      topRef.current = readTop();
    };
    // Nothing can scroll a hidden page, so polling one is pure waste. The timer
    // is torn down when the tab goes away and rebuilt when it comes back, with a
    // sample on the way out so the last visible position is not lost.
    const stop = () => {
      if (timer) {
        window.clearInterval(timer);
        timer = 0;
      }
    };
    const start = () => {
      if (!timer) timer = window.setInterval(sample, SAMPLE_MS);
    };
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        start();
      } else {
        sample();
        stop();
      }
    };
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("wheel", sample, { passive: true });
    window.addEventListener("touchmove", sample, { passive: true });
    window.addEventListener("keyup", sample);
    return () => {
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("wheel", sample);
      window.removeEventListener("touchmove", sample);
      window.removeEventListener("keyup", sample);
    };
  }, []);

  // A restore in progress is abandoned the moment the reader scrolls themselves.
  // Fighting them for the viewport is worse than losing the position.
  useEffect(() => {
    const abandon = () => {
      if (cancelRestoreRef.current) cancelRestoreRef.current();
    };
    window.addEventListener("wheel", abandon, { passive: true });
    window.addEventListener("touchstart", abandon, { passive: true });
    window.addEventListener("keydown", abandon);
    return () => {
      window.removeEventListener("wheel", abandon);
      window.removeEventListener("touchstart", abandon);
      window.removeEventListener("keydown", abandon);
    };
  }, []);

  useLayoutEffect(() => {
    // Persist the entry being left before doing anything to the new one.
    const outgoingKey = previousKeyRef.current;
    if (outgoingKey && outgoingKey !== location.key) {
      save(outgoingKey, topRef.current);
    }
    const isFirstRender = outgoingKey === null;
    // Read before the ref is updated: this is what separates a query-string
    // rewrite from a move to another page.
    const samePathname = previousPathnameRef.current === location.pathname;
    previousKeyRef.current = location.key;
    previousPathnameRef.current = location.pathname;

    // Any restore still running belongs to the previous entry.
    if (cancelRestoreRef.current) cancelRestoreRef.current();

    if (navigationType === "REPLACE" && !isFirstRender && samePathname) {
      return undefined;
    }

    if (navigationType === "POP") {
      const target = load(location.key);
      if (target != null && target > 0) {
        // Wait for the content. The data this page needs is still in flight on
        // the first frame, so the document is too short to hold the offset and
        // an immediate write would silently clamp to 0.
        const deadline = Date.now() + RESTORE_TIMEOUT_MS;
        let timerId = 0;
        let cancelled = false;
        cancelRestoreRef.current = () => {
          cancelled = true;
          window.clearTimeout(timerId);
          cancelRestoreRef.current = null;
        };
        const tick = () => {
          if (cancelled) return;
          if (canReach(target) || Date.now() > deadline) {
            // Either the content is tall enough to hold the offset, or we have
            // waited long enough and a best effort beats leaving them at the top.
            writeTop(target);
            topRef.current = readTop();
            cancelRestoreRef.current = null;
            return;
          }
          timerId = window.setTimeout(tick, RESTORE_STEP_MS);
        };
        tick();
        return () => {
          if (cancelRestoreRef.current) cancelRestoreRef.current();
        };
      }
      // Nothing saved for this entry: treat it as a fresh arrival.
    }

    writeTop(0);
    topRef.current = 0;
    return undefined;
    // location.pathname is listed for the linter's benefit. It cannot change
    // without location.key changing too, so it adds no extra runs.
  }, [location.key, location.pathname, navigationType]);

  return null;
}
